import React, { useState, useEffect } from "react";
import { getPricingData, Freshness } from "../data";

export default function Pricing() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showManual, setShowManual] = useState(true);

  useEffect(() => {
    getPricingData().then((d) => {
      setData(d);
      setLoading(false);
    });
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-400">Loading pricing data...</p>
      </div>
    );
  }

  const competitors = data?.competitors || [];
  const withPlans = competitors.filter((c) => c.plans && c.plans.length > 0);
  const withoutPlans = competitors.filter(
    (c) => !c.plans || c.plans.length === 0
  );

  function renderPlan(plans, idx) {
    if (!plans || plans.length <= idx) return <span className="text-gray-300">—</span>;
    const p = plans[idx];
    return (
      <div className="text-sm">
        <span className="text-xs text-gray-400 block">{p.plan_name}</span>
        <span className="font-medium">{p.price_monthly || "—"}</span>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h1 className="text-2xl font-bold text-gray-900">Pricing Comparison</h1>
        <span className="text-sm text-gray-500">
          {withPlans.length} / {competitors.length} with data
        </span>
      </div>
      <Freshness dateStr={data?.last_updated} label="Pricing data" />

      {/* Toggle manual data */}
      <div className="flex items-center gap-2 mt-4 mb-6">
        <label className="flex items-center gap-2 text-sm text-gray-600">
          <input
            type="checkbox"
            checked={showManual}
            onChange={() => setShowManual(!showManual)}
            className="rounded border-gray-300"
          />
          Show manually supplemented data (JS-rendered sites)
        </label>
      </div>

      {/* Pricing Table */}
      <div className="dashboard-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="text-left py-3 px-4 font-semibold text-gray-900 min-w-[160px]">Competitor</th>
              <th className="text-left py-3 px-4 font-semibold text-gray-900">Tier 1</th>
              <th className="text-left py-3 px-4 font-semibold text-gray-900">Tier 2</th>
              <th className="text-left py-3 px-4 font-semibold text-gray-900">Tier 3</th>
              <th className="text-left py-3 px-4 font-semibold text-gray-900">Enterprise</th>
              <th className="text-left py-3 px-4 font-semibold text-gray-900 min-w-[140px]">Source</th>
            </tr>
          </thead>
          <tbody>
            {/* Separator: With plans */}
            {withPlans.map((row) => {
              const plans = row.plans || [];
              return (
                <tr key={row.competitor} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-3 px-4">
                    <p className="font-medium text-gray-900">{row.competitor}</p>
                    <span className="text-xs text-gray-500">{row.category}</span>
                  </td>
                  <td className="py-3 px-4">{renderPlan(plans, 0)}</td>
                  <td className="py-3 px-4">{renderPlan(plans, 1)}</td>
                  <td className="py-3 px-4">{renderPlan(plans, 2)}</td>
                  <td className="py-3 px-4">{renderPlan(plans, 3)}</td>
                  <td className="py-3 px-4 text-xs">
                    {row.pricing_notes?.includes("public pricing") || row.pricing_notes?.includes("DNS") ? (
                      <span className="text-gray-400 bg-gray-100 px-2 py-0.5 rounded">N/A</span>
                    ) : row.pricing_notes?.includes("手动") || row.pricing_notes?.includes("manual") ? (
                      <span className="text-yellow-600 bg-yellow-50 px-2 py-0.5 rounded">Manual</span>
                    ) : (
                      <span className="text-green-600 bg-green-50 px-2 py-0.5 rounded">Auto</span>
                    )}
                  </td>
                </tr>
              );
            })}

            {/* Separator: No plans (only if showManual is true) */}
            {showManual && withoutPlans.length > 0 && (
              <>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <td colSpan={6} className="py-2 px-4 text-xs text-gray-400 font-medium">
                    Competitors without pricing data ({withoutPlans.length})
                  </td>
                </tr>
                {withoutPlans.map((row) => (
                  <tr key={row.competitor} className="border-b border-gray-100 hover:bg-gray-50 text-gray-500">
                    <td className="py-3 px-4">
                      <p className="font-medium text-gray-900">{row.competitor}</p>
                      <span className="text-xs text-gray-400">{row.category}</span>
                    </td>
                    <td colSpan={4} className="py-3 px-4 text-xs text-gray-400">
                      {row.pricing_notes || "No pricing data available"}
                    </td>
                    <td className="py-3 px-4 text-xs">
                      <span className="text-gray-400 bg-gray-100 px-2 py-0.5 rounded">N/A</span>
                    </td>
                  </tr>
                ))}
              </>
            )}
          </tbody>
        </table>
      </div>

      {/* Insights */}
      <section className="mt-8">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Pricing Insights</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="dashboard-card">
            <h3 className="font-semibold text-gray-900 mb-2">💰 Free Tier</h3>
            <p className="text-sm text-gray-600">
              Direct competitors mostly offer free tiers: Fathom (unlimited), Granola (Basic), Notta (120min/day).
              Oak's free tier needs to at minimum match Notta's threshold.
            </p>
          </div>
          <div className="dashboard-card">
            <h3 className="font-semibold text-gray-900 mb-2">🎯 Mid-market</h3>
            <p className="text-sm text-gray-600">
              Pro/Business pricing clusters around \$14-\$20/user/mo. Granola (\$14) and Otter (\$8.33) are most aggressive.
              Oak's Cantonese bilingual support justifies a premium.
            </p>
          </div>
          <div className="dashboard-card">
            <h3 className="font-semibold text-gray-900 mb-2">🚀 Enterprise</h3>
            <p className="text-sm text-gray-600">
              All majors offer custom enterprise pricing. HK data residency + compliance gives Oak a strong
              enterprise story vs US-based competitors.
            </p>
          </div>
        </div>
      </section>

      <p className="text-xs text-gray-400 mt-6">
        Auto = DeepSeek extracted from scraped pages. Manual = supplemented from public information.
        Run <code className="bg-gray-100 px-1 rounded">python3 pipeline.py</code> to refresh.
      </p>
    </div>
  );
}
